import React, { useState, useMemo } from 'react';
import { PipelineColumn } from '@/components/PipelineColumn';
import { PipelineFilters } from '@/components/PipelineFilters';
import { mockLeads } from '@/data/mockData';
import { Channel, Lead, PipelineStage } from '@/types';

interface PipelineProps {
  selectedChannel: Channel;
}

export const Pipeline: React.FC<PipelineProps> = ({ selectedChannel: _selectedChannel }) => {
  const [filtersCollapsed, setFiltersCollapsed] = useState(true);
  const [selectedLeads, setSelectedLeads] = useState<string[]>([]);
  const [filters, setFilters] = useState({
    channels: ['RETAIL', 'SL'] as Channel[],
    scoreRange: [0, 100] as [number, number],
    daysRange: [0, 30] as [number, number],
    sources: [] as string[],
    intents: [] as string[],
    states: [] as string[],
  });

  const stages: PipelineStage[] = ['New', 'Engaged', 'Stalled', 'Dropped', 'Converted'];

  // Filter leads based on selected channel and filters
  const filteredLeads = useMemo(() => {
    return mockLeads.filter(lead => {
      // Channel filter
      if (!filters.channels.includes(lead.channel)) return false;
      
      // Score filter
      if (lead.engagementScore < filters.scoreRange[0] || 
          lead.engagementScore > filters.scoreRange[1]) return false;
      
      // Additional filters would go here
      return true;
    });
  }, [filters]);

  // Group leads by stage
  const leadsByStage = useMemo(() => {
    const grouped: Record<PipelineStage, Lead[]> = {
      'New': [],
      'Engaged': [],
      'Stalled': [],
      'Dropped': [],
      'Converted': [],
    };

    filteredLeads.forEach(lead => {
      if (grouped[lead.stage]) {
        grouped[lead.stage].push(lead);
      }
    });

    return grouped;
  }, [filteredLeads]);

  const handleStageChange = (_leadId: string, _newStage: string) => {
    // In a real app, this would update the backend
  };

  const handleBulkAction = (_action: string) => {
    setSelectedLeads([]);
  };

  return (
    <div className="space-y-6 p-6">
      {/* Filters */}
      <PipelineFilters
        isCollapsed={filtersCollapsed}
        onToggle={() => setFiltersCollapsed(!filtersCollapsed)}
        onFiltersChange={setFilters}
      />

      {/* Bulk Actions Bar */}
      {selectedLeads.length > 0 && (
        <div className="bg-slate-800 border border-slate-600 rounded-lg p-4">
          <div className="flex items-center justify-between">
            <span className="text-slate-300">
              {selectedLeads.length} leads selected
            </span>
            <div className="flex space-x-2">
              <button 
                onClick={() => handleBulkAction('send_message')}
                className="px-3 py-1 bg-blue-600 text-white text-sm rounded hover:bg-blue-500"
              >
                Send Message
              </button>
              <button 
                onClick={() => handleBulkAction('change_sequence')}
                className="px-3 py-1 bg-emerald-600 text-white text-sm rounded hover:bg-emerald-500"
              >
                Change Sequence
              </button>
              <button 
                onClick={() => handleBulkAction('export')}
                className="px-3 py-1 bg-amber-600 text-white text-sm rounded hover:bg-amber-500"
              >
                Export
              </button>
              <button 
                onClick={() => handleBulkAction('schedule_call')}
                className="px-3 py-1 bg-teal-600 text-white text-sm rounded hover:bg-teal-500"
              >
                Schedule Call
              </button>
              <button 
                onClick={() => handleBulkAction('mark_priority')}
                className="px-3 py-1 bg-purple-600 text-white text-sm rounded hover:bg-purple-500"
              >
                Mark Priority
              </button>
              <button 
                onClick={() => handleBulkAction('archive')}
                className="px-3 py-1 bg-red-600 text-white text-sm rounded hover:bg-red-500"
              >
                Archive
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Pipeline Columns */}
      <div className="grid grid-cols-1 lg:grid-cols-5 gap-4 overflow-x-auto">
        {stages.map((stage) => (
          <PipelineColumn
            key={stage}
            stage={stage}
            leads={leadsByStage[stage]}
            onStageChange={handleStageChange}
          />
        ))}
      </div>

      {/* Summary Stats */}
      <div className="bg-slate-800 border border-slate-600 rounded-lg p-4">
        <div className="grid grid-cols-1 md:grid-cols-5 gap-4">
          {stages.map((stage) => (
            <div key={stage} className="text-center">
              <div className="text-2xl font-bold text-slate-50">
                {leadsByStage[stage].length}
              </div>
              <div className="text-sm text-slate-400">
                {stage} Leads
              </div>
            </div>
          ))}
        </div>
      </div>
    </div>
  );
};